import React, { useState, useEffect, useRef } from 'react';
import { useHotkeys } from '@snipit/core/hooks/useHotkeys';
import HomeView from '@snipit/ui/components/HomeView';
import CreateView from '@snipit/ui/components/CreateView';
import HelpView from '@snipit/ui/components/HelpView';
import ToolsGuideView from '@snipit/ui/components/ToolsGuideView';
import { INITIAL_SNIPPET } from '@snipit/core/utils/constants';
import { useSnippets } from '@snipit/core/hooks/useSnippets';
import { useOrganization } from '@snipit/core/hooks/useOrganization';
import { useTeams } from '@snipit/core/hooks/useTeams';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@snipit/core/context/AuthContext';

export default function VaultView() {
    const { organization } = useOrganization();
    const { teams } = useTeams();
    const { userLicense } = useAuth();
    const navigate = useNavigate();
    const { snippets, addSnippet, updateSnippet, removeSnippet, removeSnippets, fetchSnippetContent, migrateData, syncData } = useSnippets(organization, teams);
    const [view, setView] = useState('home');
    const [currentVault, setCurrentVault] = useState('dev');
    const [newSnippet, setNewSnippet] = useState(INITIAL_SNIPPET);

    const [devTabs, setDevTabs] = useState(['All', 'AppSheet', 'Sheets', 'HTML', 'Javascript']);
    const [aiTabs, setAiTabs] = useState(['All', 'Prompt', 'Gem', 'System Prompt']);
    const [linkTabs, setLinkTabs] = useState(['All', 'GitHub Repo', 'Dev Docs', 'Applications']);
    const [toolTabs, setToolTabs] = useState(['All', 'JWT Decoder', 'JSON Formatter', 'Boilerplate Gen', 'Regex Tester', 'Base64 & URL', 'Color Converter', 'Hash Generator', 'Mock Data Gen', 'Case Converter', 'Markdown Pad']);
    const searchRef = useRef(null);

    useEffect(() => {
        chrome.storage.local.get(['draft', 'devTabs', 'aiTabs', 'linkTabs', 'toolTabs', 'userTabs', 'currentVault'], (res) => {
            if (res.draft) setNewSnippet(res.draft);
            if (res.devTabs) setDevTabs(res.devTabs);
            else if (res.userTabs) setDevTabs(res.userTabs);
            if (res.aiTabs) setAiTabs(res.aiTabs);
            if (res.linkTabs) setLinkTabs(res.linkTabs);
            if (res.toolTabs) setToolTabs(res.toolTabs);
            if (res.currentVault) setCurrentVault(res.currentVault);
        });
    }, []);

    useEffect(() => { chrome.storage.local.set({ draft: newSnippet }); }, [newSnippet]);
    useEffect(() => { 
        chrome.storage.local.set({ currentVault }); 
        window.dispatchEvent(new CustomEvent('vaultChanged', { detail: currentVault }));
    }, [currentVault]);

    useEffect(() => {
        const handleOpenHelp = () => setView('help');
        window.addEventListener('openHelp', handleOpenHelp);
        return () => window.removeEventListener('openHelp', handleOpenHelp);
    }, [setView]);

    const theme = {
        bgPrimary: currentVault === 'ai' ? 'bg-purple-500' : currentVault === 'links' ? 'bg-emerald-500' : currentVault === 'tools' ? 'bg-amber-500' : 'bg-blue-500',
        bgHover: currentVault === 'ai' ? 'hover:bg-purple-400' : currentVault === 'links' ? 'hover:bg-emerald-400' : currentVault === 'tools' ? 'hover:bg-amber-400' : 'hover:bg-blue-400',
        bgSubtle: currentVault === 'ai' ? 'bg-purple-400/10' : currentVault === 'links' ? 'bg-emerald-400/10' : currentVault === 'tools' ? 'bg-amber-400/10' : 'bg-blue-400/10',
        borderSubtle: currentVault === 'ai' ? 'border-purple-400/20' : currentVault === 'links' ? 'border-emerald-400/20' : currentVault === 'tools' ? 'border-amber-400/20' : 'border-blue-400/20',
        textPrimary: currentVault === 'ai' ? 'text-purple-500' : currentVault === 'links' ? 'text-emerald-500' : currentVault === 'tools' ? 'text-amber-500' : 'text-blue-500',
        textHover: currentVault === 'ai' ? 'hover:text-purple-400' : currentVault === 'links' ? 'hover:text-emerald-400' : currentVault === 'tools' ? 'hover:text-amber-400' : 'hover:text-blue-400',
        shadowPrimary: currentVault === 'ai' ? 'shadow-purple-500/50' : currentVault === 'links' ? 'shadow-emerald-500/50' : currentVault === 'tools' ? 'shadow-amber-500/50' : 'shadow-blue-500/50',
    };

    const handleSave = async () => {
        let finalContent = newSnippet.content;

        if (newSnippet.type === 'Gem') {
            if (!newSnippet.gemPremise && !newSnippet.gemPersona && !newSnippet.gemContext && !newSnippet.gemRules) {
                alert("⚠️ You cannot save an empty Gem! Please fill out at least one section.");
                return;
            }
            let compiled = '';
            if (newSnippet.gemPremise) compiled += `### Premise\n${newSnippet.gemPremise}\n\n`;
            if (newSnippet.gemPersona) compiled += `### Persona\n${newSnippet.gemPersona}\n\n`;
            if (newSnippet.gemContext) compiled += `### Context\n${newSnippet.gemContext}\n\n`;
            if (newSnippet.gemRules) compiled += `### Rules\n${newSnippet.gemRules}\n\n`;
            finalContent = compiled.trim();
        } else if (newSnippet.type === 'Few-Shot') {
            finalContent = `Pattern: ${newSnippet.pattern}\n\nExamples:\n${newSnippet.content}`;
        } else if (newSnippet.type === 'Context' && newSnippet.source) {
            finalContent = `Source: ${newSnippet.source}\n\n${newSnippet.content}`;
        } else if (newSnippet.type === 'JWT Decoder' || newSnippet.type === 'JSON Formatter') {
            finalContent = newSnippet.content;
        }

        if (currentVault !== 'links' && (!finalContent || !finalContent.trim())) {
            alert("⚠️ Content cannot be empty.");
            return;
        } else if (currentVault === 'links' && !(newSnippet.url || '').trim() && (!finalContent || !finalContent.trim())) {
            alert("⚠️ You must provide either a URL or some notes/commands.");
            return;
        }

        const secretPatterns = [
            { name: "Google Cloud API Key (AIza)", regex: /AIza[0-9A-Za-z-_]{35}/ },
            { name: "OpenAI API Key (sk-...)", regex: /sk-[A-Za-z0-9-_]{32,}/ },
            { name: "AWS Access Key", regex: /AKIA[0-9A-Z]{16}/ },
            { name: "GitHub Token", regex: /gh[pousr]_[A-Za-z0-9_]{36}/ },
            { name: "Private Key", regex: /-----BEGIN (RSA|OPENSSH|DSA|EC|PGP) PRIVATE KEY-----/ }
        ];

        const stringToScan = [newSnippet.title, newSnippet.url, finalContent].join(' ');
        let detectedSecret = null;

        for (const pattern of secretPatterns) {
            if (pattern.regex.test(stringToScan)) {
                detectedSecret = pattern.name;
                break;
            }
        }

        if (detectedSecret) {
            const proceed = window.confirm(
                `🛡️ Enterprise Security Warning:\n\nA potential [ ${detectedSecret} ] was detected in your snippet.\n\nAre you sure you want to save this to your vault?`
            );
            if (!proceed) return;
        }

        const finalTitle = newSnippet.title.trim() ? newSnippet.title : "Untitled Item";
        const existing = snippets.find(s => s.id === newSnippet.id);
        let historyList = existing ? (existing.history || []) : [];

        if (existing && existing.content !== finalContent) {
            historyList = [existing.content, ...historyList].slice(0, 5);
        }

        let orgIdToSave = newSnippet.orgId;
        if ((newSnippet.visibility === 'organization' || newSnippet.visibility === 'team') && organization) {
            orgIdToSave = organization.id;
        }

        const snippetToSave = {
            ...newSnippet,
            title: finalTitle, content: finalContent,
            history: historyList, vault: newSnippet.vault || currentVault,
            orgId: orgIdToSave
        };

        try {
            if (existing) {
                // Remove id from object to prevent storing it twice
                const { id, ...updates } = snippetToSave;
                await updateSnippet(existing.id, updates);
            } else {
                await addSnippet(snippetToSave);
            }
            
            setView('home');
            setNewSnippet({ ...INITIAL_SNIPPET, type: currentVault === 'dev' ? 'AppSheet' : currentVault === 'ai' ? 'Prompt' : currentVault === 'tools' ? 'JWT Decoder' : 'GitHub Repo', vault: currentVault });
        } catch (error) {
            console.error("Error saving snippet:", error);
            alert("Failed to save snippet to cloud.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            await removeSnippet(id);
            if (view === 'create' && newSnippet.id === id) {
                setView('home');
                setNewSnippet({ ...INITIAL_SNIPPET, type: currentVault === 'dev' ? 'AppSheet' : currentVault === 'ai' ? 'Prompt' : currentVault === 'tools' ? 'JWT Decoder' : 'GitHub Repo', vault: currentVault });
            }
        } catch (error) {
            console.error("Error deleting snippet:", error);
            alert("Failed to delete snippet.");
        }
    };

    const handleTogglePublic = async (snippet) => {
        try {
            await updateSnippet(snippet.id, { isPublic: !snippet.isPublic });
        } catch (error) {
            console.error("Failed to toggle public state:", error);
            alert("Failed to update visibility.");
        }
    };

    useHotkeys({
        'ctrl+k': () => searchRef.current?.focus(),
        'ctrl+n': () => { setNewSnippet({ ...INITIAL_SNIPPET, type: currentVault === 'dev' ? 'AppSheet' : currentVault === 'ai' ? 'Prompt' : currentVault === 'tools' ? 'JWT Decoder' : 'GitHub Repo', vault: currentVault }); setView('create'); },
        'ctrl+s': () => { if (view === 'create') handleSave(); },
        'esc': () => setView('home')
    });

    const handleCodeReview = async (snippet) => {
        let content = snippet.content;
        if (!content && fetchSnippetContent) {
            const data = await fetchSnippetContent(snippet.id, snippet.userId);
            if (data) content = data.content;
        }
        navigate('/review', { state: { code: content || '', language: snippet.type } });
    };

    return (
        <div className="w-full h-full bg-[#121212] text-gray-300 flex flex-col overflow-y-auto p-6 lg:p-8">
            {view === 'home' ? (
                <HomeView
                    isWebDashboard={true}
                    setView={setView} snippets={snippets} searchRef={searchRef}
                    setNewSnippet={setNewSnippet} handleDelete={handleDelete} handleTogglePublic={handleTogglePublic}
                    currentVault={currentVault} setCurrentVault={setCurrentVault}
                    devTabs={devTabs} setDevTabs={setDevTabs} aiTabs={aiTabs} setAiTabs={setAiTabs}
                    linkTabs={linkTabs} setLinkTabs={setLinkTabs} toolTabs={toolTabs} setToolTabs={setToolTabs} 
                    handleDeleteBulk={removeSnippets} theme={theme} userLicense={userLicense}
                    addSnippet={addSnippet} fetchSnippetContent={fetchSnippetContent}
                    onCodeReview={(userLicense?.tier === 'enterprise' || userLicense?.tier === 'enterprise_org') ? handleCodeReview : undefined}
                    onSync={syncData}
                />
            ) : view === 'help' ? (
                <HelpView setView={setView} theme={theme} />
            ) : view === 'tools_guide' ? (
                <ToolsGuideView setView={setView} />
            ) : (
                <CreateView
                    newSnippet={newSnippet} setNewSnippet={setNewSnippet}
                    onSave={handleSave} onCancel={() => setView('home')}
                    handleDelete={handleDelete}
                    theme={theme} organization={organization} teams={teams}
                    userLicense={userLicense}
                />
            )}

        </div>
    );
}
